import React, { useEffect, useState } from 'react'
import moment from 'moment'

import {
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from '@material-ui/core'
import releaseData from './data.json'

interface FeatureProps {
  order?: number
  id: string
  title: string
  bullets: string[]
  dismiss?: (id: string) => void
}

const Feature = (props: FeatureProps) => {
  const { order, id, title, bullets, dismiss } = props
  return (
    <Card>
      <CardContent>
        <Typography variant="h3">
          {order}. {title}
          {'        '}
          <Button variant="outlined" size="small" onClick={() => dismiss?.(id)}>
            Dismiss
          </Button>
        </Typography>
        <List>
          {bullets.map((bullet, index) => (
            <ListItem key={bullet}>
              {index + 1}. {bullet}
            </ListItem>
          ))}
        </List>
      </CardContent>
      <CardActions></CardActions>
    </Card>
  )
}

interface ReleaseDialogProps {
  open: boolean
  onClose: () => void
  features: FeatureProps[]
  dismiss: (id: string) => void
}

const ReleaseDialog = (props: ReleaseDialogProps) => {
  const { open, onClose, features, dismiss } = props

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h1">Recent Changes to SKUit</Typography>
        <Typography variant="h4">
          Click &quot;Dismiss&quot; to permanently dismiss each item. <br /> Otherwise they will
          continue to display each time you reload.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {features.map((feature, index) => {
          feature.order = index + 1
          return <Feature key={feature.id} dismiss={dismiss} {...feature} />
        })}
      </DialogContent>
    </Dialog>
  )
}

const dismissalKey = 'dismissedReleases'

export const Releases = () => {
  const [open, setOpen] = useState(false)
  const [dismissedReleases, setDismissedReleases] = useState<string[]>([])
  const [releases, setReleases] = useState<FeatureProps[]>([])

  const dismiss = (id: string) => {
    const newDismissals = [...dismissedReleases, id]
    setDismissedReleases(newDismissals)
    const newStoredDismissalsString = newDismissals.join(',')
    localStorage.setItem(dismissalKey, newStoredDismissalsString)
    const filteredReleases = releases.filter((release) => !newDismissals.includes(release.id))
    setReleases(filteredReleases)
    if (!filteredReleases.length) {
      setOpen(false)
    }
  }

  useEffect(() => {
    const releaseIds = releaseData.features.map((feature) => feature.id)
    const storedDismissalsString = localStorage.getItem(dismissalKey) || ''
    const storedDismissals = (storedDismissalsString.split(',') || []).reduce(
      (array: string[], id) => {
        if (releaseIds.includes(id)) {
          array.push(id)
        }
        return array
      },
      []
    )
    const newStoredDismissalsString = storedDismissals.join(',')
    if (newStoredDismissalsString !== storedDismissalsString) {
      localStorage.setItem(dismissalKey, newStoredDismissalsString)
    }
    setDismissedReleases(storedDismissals)
    const now = moment()
    const filteredReleases = releaseData.features.filter((feature) => {
      const expires = moment(feature.expires)
      return !storedDismissals.includes(feature.id) && now < expires
    })
    setReleases(filteredReleases)
    if (filteredReleases.length) {
      setOpen(true)
    }
  }, [])

  return (
    <>
      <ReleaseDialog
        open={open}
        onClose={() => setOpen(false)}
        features={releases}
        dismiss={dismiss}
      />
    </>
  )
}
